import React, { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import "modules/root/services/locale";

import "normalize.css";
import "@blueprintjs/icons/lib/css/blueprint-icons.css";
import "@blueprintjs/select/lib/css/blueprint-select.css";
import "@blueprintjs/datetime/lib/css/blueprint-datetime.css";
import "modules/root/styles/theme/blueprint/blueprint.scss";
import "react-month-picker/css/month-picker.css";
import "rc-steps/assets/index.css";
import "../node_modules/react-grid-layout/css/styles.css";
import "../node_modules/react-resizable/css/styles.css";

import "modules/root/styles/planr/index.scss";
import "lightbox-react/style.css";
import "./modules/root/styles/index.scss";
import "./modules/root/styles/theme/index.scss";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-balham.css";

// регистрация модулей AG Grid — должна выполниться до первого грида
import "modules/common/services/table/ag-grid-setup";

import { applySnapshot } from "mobx-state-tree";

import moment from "moment";
import "moment/locale/ru";

import App from "modules/root/components/app/App";
import { Unavailable } from "modules/root/components/app/Unavailable";

import { nameof } from "modules/common/services/typescript";
import {
    buildHttpTransport,
    HttpError,
    NotAuthorized,
    ConfirmationWaiting,
    Locked,
} from "modules/common/services/communication/http";

import { Constants } from "modules/root/models/constants";
import { ApplicationStore, initialState, StoreWrapper } from "modules/root/models/store";
import { Notifier } from "modules/common/models/notificator";
import { apiUrls } from "modules/common/services/communication/urls";

import { RootCache } from "modules/common/services/cache";
import { NotificationType } from "modules/common/services/notifications/types";
import { AppVersion } from "modules/common/services/app";
import { apiUrl as devApiUrl } from "./devApi";

moment.locale("ru");

// Корень один на всё приложение: рисовать в него приходится из двух веток —
// после загрузки метаданных и при её ошибке, а повторный createRoot на том же
// контейнере React 18 не допускает
const root = createRoot(document.getElementById("root")!);

// адрес стенда живёт в REACT_APP_API_URL, но ходим мы туда через dev-прокси:
// куку чужого домена браузер бы не отдал (см. devApi.ts)
const apiUrl = devApiUrl(import.meta.env.REACT_APP_API_URL, window.location.hostname, import.meta.env.DEV);
fetch(apiUrl + apiUrls.application.metadata, {
    credentials: "include",
})
    .then((r) => r.json())
    .then((metadata: AppMetadata) => {
        // restore latestOrderId from local cache
        const latestOrderId = RootCache.get(
            nameof((a: AppMetadata) => a.latestOrderId),
            ""
        );

        if (latestOrderId) {
            metadata.latestOrderId = latestOrderId;
        }

        run({
            url: apiUrl,
            ...metadata,
        });
    })
    .catch(() => {
        root.render(<Unavailable />);
    });

async function run(config: AppConfig) {
    const version = AppVersion();

    const wrapper: StoreWrapper = {
        store: null,
    };
    const notificator = buildNotificatior(wrapper);

    const http = buildHttpTransport(config, (status) => {
        if (wrapper.store && status === NotAuthorized) {
            wrapper.store.session.loggedIn && wrapper.store.session.setPopupStaste(true);
        }

        // устройство не подтверждено: окно входа откроется на шаге ввода кода
        if (wrapper.store && status === ConfirmationWaiting) {
            wrapper.store.session.setLoggedInState(false);
            wrapper.store.session.setConfirmation(true);
        }

        if (wrapper.store && status === Locked) {
            wrapper.store.session.setLoggedInState(false);
            wrapper.store.session.setConfirmation(false);
            notificator.error("Вход с этого устройства заблокирован администратором");
        }
    });

    const appStore = ApplicationStore.create(initialState(config, version), {
        http,
        notificator,
    });
    wrapper.store = appStore;

    // очищаем состояние после выхода
    const onLogout = () =>
        applySnapshot(
            appStore,
            initialState(
                {
                    ...config,
                    access: [],
                    latestOrderId: "",
                    userId: "",
                    userLogin: "",
                    userName: "",
                },
                version
            )
        );

    wrapper.store = appStore;

    config.constants && applySnapshot(Constants, config.constants);

    await wrapper.store.setupConnection(config.url);
    if (wrapper.store.session.loggedIn) {
        await wrapper.store.loadChangelog();
        await wrapper.store.loadAnnounces();
    }
    if (config.waitConfirmation) {
        wrapper.store.session.setConfirmation(true);
    }
    // StrictMode действует только в dev: дважды вызывает рендер и эффекты,
    // чтобы код, который не убирает за собой, проявился до React 19
    root.render(
        <StrictMode>
            <App store={wrapper.store} onLogout={onLogout} />
        </StrictMode>
    );
}

const buildNotificatior = (wrapper: StoreWrapper): Notifier => {
    const showMessage = (type: NotificationType, message: string) => {
        if (wrapper.store) {
            wrapper.store.notifications.addNotification(type, message);
        }
    };

    return {
        error(message: any) {
            const text = typeof message === "string" ? message : message.message;

            const suppress =
                message instanceof HttpError && [NotAuthorized, ConfirmationWaiting, Locked].includes(message.code)
                    ? true
                    : false;

            !suppress && showMessage("error", text);
        },
        info(message: string) {
            showMessage("info", message);
        },
        success(message: string) {
            showMessage("success", message);
        },
        warning(message: string) {
            showMessage("warning", message);
        },
    };
};
